import { motion } from 'framer-motion';

const planTypes = [
  {
    icon: 'ri-government-line',
    title: 'Ontario Drug Benefit (ODB)',
    desc: 'Eligible Ontario residents — including seniors aged 65+, residents of long-term care homes, those receiving Ontario Works or ODSP, and recipients of the Trillium Drug Program — have their prescription costs covered through the Ontario Drug Benefit program. Our pharmacy processes ODB claims directly with no upfront payment required from eligible patients.',
  },
  {
    icon: 'ri-bank-card-line',
    title: 'Private Insurance Plans',
    desc: 'We accept and direct-bill virtually all major Canadian insurance providers, including Green Shield, Sun Life, Manulife, Desjardins, Blue Cross, Great-West Life, Canada Life, Medavie Blue Cross, and others. Our real-time claims processing means you only pay your co-pay or deductible at the counter — no waiting for reimbursement.',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Trillium Drug Program',
    desc: 'For Ontario households facing high prescription drug costs relative to income, the Trillium Drug Program provides financial assistance. Our pharmacy team can help you understand eligibility requirements, assist with the application process, and bill directly through the program once you are enrolled.',
  },
  {
    icon: 'ri-scales-line',
    title: 'Generic Substitution & Cost Savings',
    desc: 'Whenever clinically appropriate, our pharmacists proactively recommend generic alternatives that are chemically identical to brand-name medications — offering the same safety and effectiveness at a fraction of the cost. We always consult you before making any substitution and ensure you are comfortable with the choice.',
  },
];

export default function PharmacyInsurance() {
  return (
    <section aria-labelledby="insurance-heading" className="mb-20">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="mb-10"
      >
        <p className="text-xs font-semibold text-primary-600 uppercase tracking-[0.15em] mb-4">Coverage &amp; Billing</p>
        <h2 id="insurance-heading" className="text-3xl font-bold text-foreground-950 mb-4 leading-snug">
          Insurance Coverage &amp; Transparent Pricing
        </h2>
        <p className="text-foreground-500 max-w-3xl text-[0.94rem] leading-relaxed">
          Navigating prescription drug coverage can be confusing. At <strong>Thornhill Drug-Mart</strong>, our experienced pharmacy team handles the administrative burden so you can focus on your health. We work with provincial drug plans, private insurers, and patient assistance programs to minimize your out-of-pocket costs and maximize your coverage.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
        {planTypes.map((item, i) => (
          <motion.article
            key={i}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: i * 0.08, ease: 'easeOut' }}
            className="bg-background-50 rounded-lg p-6 border border-background-200 hover:border-primary-200 transition-colors"
          >
            <div className="flex items-center gap-4 mb-3">
              <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-primary-600 text-lg`}></i>
              </div>
              <h3 className="text-base font-semibold text-foreground-950">{item.title}</h3>
            </div>
            <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
          </motion.article>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.3, ease: 'easeOut' }}
        className="bg-background-100 rounded-lg p-6 border border-background-200"
      >
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
            <i className="ri-information-line text-white text-lg"></i>
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground-950 mb-2">Not Sure About Your Coverage?</h3>
            <p className="text-sm text-foreground-600 leading-relaxed mb-3">
              Bring in your insurance card or drug benefit information and our team will verify your coverage on the spot — usually within minutes. If your plan requires prior authorization for a specific medication, our pharmacists can initiate that process with your prescriber and insurer on your behalf, saving you time and hassle.
            </p>
            <p className="text-sm text-foreground-600 leading-relaxed">
              We also offer competitive cash pricing for patients without insurance coverage. Contact us at{' '}
              <a href="tel:9057099985" className="text-primary-600 font-semibold hover:text-primary-700 cursor-pointer">(905) 709-9985</a>
              {' '}to discuss pricing for any medication — we are always transparent about costs before dispensing.
            </p>
          </div>
        </div>
      </motion.div>
    </section>
  );
}